import { ArrowRight, MessageCircle, MapPin } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { COMPANY, waLink } from "@/lib/constants";
import { useSiteAssets } from "@/context/SiteAssetsContext";

export default function Hero() {
  const { t } = useLang();
  const { images } = useSiteAssets();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[92vh] md:min-h-screen w-full overflow-hidden bg-neutral-950 pt-16 md:pt-20"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src={images.hero}
          alt=""
          className="h-full w-full object-cover hero-zoom"
        />
        {/* Left-weighted dark gradient for text contrast */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute inset-0 grain" />
      </div>

      {/* Red vertical stripe accent */}
      <div className="absolute top-24 md:top-32 right-6 md:right-12 w-[3px] h-28 md:h-40 bg-[#E11D2E] z-10" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-24 pb-24 md:pb-40 flex flex-col justify-center min-h-[78vh]">
        <div className="max-w-4xl">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <span className="h-[2px] w-10 bg-[#1F9D40]" />
            <span className="text-xs md:text-sm uppercase tracking-[0.22em] text-white/80 font-semibold">
              {t("hero.eyebrow")}
            </span>
          </div>

          {/* H1 */}
          <h1
            data-testid="hero-title"
            className="font-display font-black text-white text-[40px] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[88px] tracking-[-0.035em]"
          >
            {t("hero.title_l1")}{" "}
            <span className="text-[#1F9D40]">{t("hero.title_highlight")}</span>
            <br />
            {t("hero.title_l2")}
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 md:mt-8 text-base md:text-lg text-white/85 max-w-2xl leading-relaxed"
          >
            {t("hero.subtitle")}
          </p>

          <div className="mt-8 md:mt-10 flex flex-wrap gap-3 md:gap-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("contact")}
              className="group inline-flex items-center gap-2 bg-[#1F9D40] hover:bg-[#177d33] text-white font-bold px-6 md:px-8 py-4 rounded-full text-sm md:text-base tracking-tight transition-all duration-300 shadow-lg shadow-green-900/30 hover:shadow-green-900/50 hover:-translate-y-0.5"
            >
              {t("hero.cta_primary")}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-cta-whatsapp"
              href={waLink("Hola Modolo, me gustaría pedir un presupuesto.")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/25 backdrop-blur-sm font-semibold px-6 md:px-8 py-4 rounded-full text-sm md:text-base transition-all"
            >
              <MessageCircle size={18} />
              {t("hero.cta_secondary")}
            </a>
          </div>

          {/* Location badge */}
          <div className="mt-10 md:mt-14 inline-flex items-center gap-2 text-xs md:text-sm text-white/70">
            <MapPin size={14} className="text-[#E11D2E]" />
            <span>{COMPANY.address_line1} · {COMPANY.address_line2}</span>
          </div>
        </div>
      </div>

      {/* Bottom data row */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 backdrop-blur-sm bg-black/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-5 grid grid-cols-3 gap-4 text-white">
          <div>
            <div className="font-display font-extrabold text-2xl md:text-3xl leading-none">
              {t("trust.years_num")}
              <span className="text-[#E11D2E]">+</span>
            </div>
            <div className="text-[10px] md:text-xs uppercase tracking-wider text-white/60 mt-1">
              {t("trust.years_label")}
            </div>
          </div>
          <div>
            <div className="font-display font-extrabold text-2xl md:text-3xl leading-none">
              {t("trust.warranty_num")}
              <span className="text-[#1F9D40]"> · </span>
              <span className="text-sm md:text-base font-semibold">años</span>
            </div>
            <div className="text-[10px] md:text-xs uppercase tracking-wider text-white/60 mt-1">
              {t("trust.warranty_label")}
            </div>
          </div>
          <div>
            <div className="font-display font-extrabold text-2xl md:text-3xl leading-none">
              {t("trust.area_num")}
            </div>
            <div className="text-[10px] md:text-xs uppercase tracking-wider text-white/60 mt-1">
              {t("trust.area_label")}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
